import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { cloneDeep } from 'lodash';

import {
	DialogueActsList,
	MeetingPlayer,
	SummaryList,
	Skeleton,
	makeDefaultSummaryBlocks,
	SummaryMeta,
	AppPageLayout,
	Tabs,
	Tab,
	Space,
	Button,
	FileView,
	AgendaList,
	IconButton,
	Popover,
	TaskList,
	extractTasks,
} from '@components';

import {
	ConvertedDialogueAct,
	ConvertTranscriptFromApi,
	ProcessTranscript,
	downloadFile,
	UserRights,
	setPageTitle,
	GUID,
	useModal,
	secondsToTicks,
	ToastFastInterval,
	IMeetingUtt,
	
} from '@common';

import {
	useUserStoreContext,
} from '@store';

import {
	MeetingInfo,
	apiInstance,
	UpdateSummaryRequest,
	UpdateMeetingInfoRequest,
	LocationInfo,
	TranscriptRequestOptions,
	DuplicateRequest,
	FileParameter,
	MeetingStatuses,
	SummaryBlock,
	MeetingActions,
	FileShortInfo,
	Agenda,
	ToggleShowAgendaRequest,
	RecordingUpdateInfo,
	ITranscriptUtterance,
	MeetingTranscript_Request,
	TranscriptTextJson,
	SpeakerTranscriptJson,
	SpeakerTranscriptJson_Speaker,
	SpeakerTranscriptJson_Utt,
	ITranscriptTextJson,
} from '@api';

import { toast } from 'react-toastify';

import './MeetingPage.scss';
import { authService } from '@services';
import { MeetingDevModal, SendTasksModal } from '@modals';
import { routes } from '@routes';
import { INotifiedPage } from '../../common';
import { DownloadIcon, ListIcon } from '@icons';
import { ParticipantInfo } from '../../../SummeetClient';
import { MeetingPageHeader } from './MeetingPageHeader';

const processSummaryBlocks = (summaryBlocks: SummaryBlock[], agenda: Agenda) => {
	summaryBlocks.forEach((block) => {
		block.__updateData(agenda);
	});
}

function MeetingPage(props: INotifiedPage): JSX.Element {
	const
	location = useLocation(),
	pathParts = location.pathname.split('/'),
	meetingId = pathParts[pathParts.length - 1],
	{ state: UserStoreState } = useUserStoreContext();

	const { registerNotificationHandler, unRegisterNotificationHandler } = props;

	const
		[dialogueActs, setDialogueActs] = useState<ConvertedDialogueAct[]>(null),
		[meetingInfo, __setMeetingInfo] = useState<MeetingInfo>(null),
		[needScroll, setNeedScroll] = useState<boolean>(true),
		[isPlaying, setIsPlaying] = useState<boolean>(false),
		[isDownloadingTranscript, setIsDownloadingTranscript] = useState<boolean>(false),
		[isDownloadingTranscriptJson, setIsDownloadingTranscriptJson] = useState<boolean>(false),
		[isDownloadingResume, setIsDownloadingResume] = useState<boolean>(false),
		[userFileNames, setUserFileNames] = useState<FileShortInfo[]>([]),
		[accessToken, setAccessToken] = useState<string>(null),
		[isGeneratingAutoResume, setIsGeneratingAutoResume] = useState<boolean>(false),
		[transcriptRequestOptions, setTranscriptRequestOptions] = useState(null),
		playerBoxRef = useRef<HTMLVideoElement>(null);

	const {
		isModalOpen: isDevModalOpen,
		closeModal: closeDevModal,
		openModal: openDevModal,
	} = useModal();

	const {
		isModalOpen: isSendTasksModalOpen,
		closeModal: closeSendTasksModal,
		openModal: openSendTasksModal,
	} = useModal();

	const history = useHistory();
	const fragmentPlayTimer = useRef<NodeJS.Timeout>(null);

	

	const setMeetingInfo = useCallback((info: MeetingInfo) => {	
		if (info && info.summaryBlocks && info.summaryBlocks) {
			processSummaryBlocks(info.summaryBlocks, info.agenda);
		}
		if (info && info.transcriptText) {
			if (transcriptRequestOptions == null || info.transcriptText != meetingInfo.transcriptText) {
				const transcriptText = info.transcriptText as unknown as TranscriptTextJson;

				setTranscriptRequestOptions(new TranscriptRequestOptions({
					mergeUtts: transcriptText?.speakers?.length > 1,
					reconstructUtts: transcriptText?.speakers?.length == 1,
				}));
				
			}
			// onReprocessTranscript(info);
		}
		__setMeetingInfo(info);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [__setMeetingInfo]);

	useEffect(() => {
		if (transcriptRequestOptions == null || meetingInfo == null) {
			return;
		}

		const transcriptText = meetingInfo.transcriptText as unknown as TranscriptTextJson;
		if (!meetingInfo.transcriptText['speakers']) {
			setDialogueActs([]);
			return;
		}
		const meetingTranscriptBySpeaker = transcriptText.speakers.map((speakerTranscript) => {
			const rows = speakerTranscript.rows as undefined as ITranscriptUtterance[];
			return rows.map((row) => {
				return {
					...row,
					speaker: speakerTranscript.speaker,
				} as IMeetingUtt;
			});
		});

		const meetingTranscript = meetingTranscriptBySpeaker.flat(1).sort((a, b) => a.StartTicks - b.StartTicks);

		const processedTranscript = ProcessTranscript(meetingTranscript, transcriptRequestOptions);		

		const convertedTranscript = ConvertTranscriptFromApi(processedTranscript);

		setDialogueActs(convertedTranscript);

	}, [meetingInfo, transcriptRequestOptions]);

	const onGetNewMeetingInfo = useCallback((newMeetingInfo: MeetingInfo) => {
		setMeetingInfo(newMeetingInfo);
		setPageTitle(newMeetingInfo.subject);
		// onReprocessTranscript(newMeetingInfo);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchMeetingInfo = useCallback(async () => {
		// try
		// {
			const meetingInfo = await apiInstance.getMeetingInfo(meetingId);
			onGetNewMeetingInfo(meetingInfo);

			const new_access_token = await authService.getAccessToken();
			if (new_access_token != accessToken) {
				setAccessToken(new_access_token);
			}

			let visitedMeetingIds = JSON.parse(localStorage.getItem('visitedMeetingIds')) as string[] || [];
			if (!visitedMeetingIds.includes(meetingId)) {
				if (meetingInfo.status.alias == MeetingStatuses.New) {
					visitedMeetingIds.push(meetingId);
					localStorage.setItem('visitedMeetingIds', JSON.stringify(visitedMeetingIds));
				}
			} else {
				if (meetingInfo.status.alias != MeetingStatuses.New) {
					visitedMeetingIds = visitedMeetingIds.filter(m => m != meetingId);
					localStorage.setItem('visitedMeetingIds', JSON.stringify(visitedMeetingIds));
				}
			}

			const userFileNamesApi = await apiInstance.getUserFiles(meetingInfo.id);
			setUserFileNames(userFileNamesApi);
		// }
		// catch(ex)
		// {
		// 	if (ApiException.isApiException(ex))
		// 	{
		// 		toast(`${ex}`, {autoClose: false });
		// 	}
		// 	else {
		// 		toast(`${ex}`, {autoClose: false });
		// 	}
		// }
	}, [accessToken, meetingId, onGetNewMeetingInfo]);

	useEffect(() => {
		setPageTitle('');
		fetchMeetingInfo();

		const notifCallback = (updatedMeetingId) => {
			if (updatedMeetingId == meetingId) {
				toast.info(
					<>Собрание было изменено. <br/>Нажмите здесь, чтобы обновить страницу (несохранённые данные будут потеряны)</>, {
						autoClose: false,
						hideProgressBar: true,
						onClick: () => {
							window.location.reload();
						}
					}
				);
			}
		}

		registerNotificationHandler("MeetingStatusChange", notifCallback);
		return () => {
			unRegisterNotificationHandler("MeetingStatusChange", notifCallback);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const handleKeyDown = (ev) => {	
			if (ev.key == 'F9' && UserStoreState.rights.includes(UserRights.developer)) {
				const newMode = !isDevModalOpen;
	
				if (newMode) {
					openDevModal();
				} else {
					closeDevModal();
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown);		
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		}
	}, [UserStoreState, isDevModalOpen, openDevModal, closeDevModal])

	// handlers
	const onPauseDAClick = useCallback((): void => {
		playerBoxRef.current.pause();
	}, [playerBoxRef]);

	const toggleNeedScroll = useCallback(() => {
		setNeedScroll(!needScroll);
	}, [needScroll, setNeedScroll]);

	const onPlayerPlayStateChanged = useCallback((isPlaying: boolean): void => {
		setIsPlaying(isPlaying);
	}, [setIsPlaying]);

	const onPlayerTimeChanged = useCallback((seconds: number): void => {
		
		const playerTicks = secondsToTicks(seconds);

		const newDialogueActs = dialogueActs.map((dialogueAct) => {
			const newDialogueAct: ConvertedDialogueAct = {
				...dialogueAct,
				isPlaying: playerTicks >= dialogueAct.data.timeTicks && playerTicks <= dialogueAct.data.timeEndTicks
			};
			return newDialogueAct;
		});

		setDialogueActs(newDialogueActs);
		
	}, [dialogueActs]);

	const onHighlightihgChanged = useCallback((ids: string[]) => {

		const newDialogueActs = dialogueActs.map((dialogueAct) => {
			const newDialogueAct: ConvertedDialogueAct = {
				...dialogueAct,
				isHighlighted: ids.includes(dialogueAct.data.id)
			};
			return newDialogueAct;
		});

		setNeedScroll(false);
		setDialogueActs(newDialogueActs);
	}, [dialogueActs]);

	const onPlayDAClick = useCallback((startTimeSeconds: number, endTimeSeconds: number): void => {

		playerBoxRef.current.currentTime = startTimeSeconds ? startTimeSeconds : playerBoxRef.current.currentTime;
		onPlayerTimeChanged(playerBoxRef.current.currentTime);
		playerBoxRef.current.play();

		if (fragmentPlayTimer.current) {
			clearInterval(fragmentPlayTimer.current);
			fragmentPlayTimer.current = null;
		}

		fragmentPlayTimer.current = setInterval(() => {
			if (!playerBoxRef.current) {
				clearInterval(fragmentPlayTimer.current);
				fragmentPlayTimer.current = null;
				return;
			}

			if (playerBoxRef.current.currentTime >= endTimeSeconds) {
				clearInterval(fragmentPlayTimer.current);
				fragmentPlayTimer.current = null;
				playerBoxRef.current.pause();
			}
		}, 40);


	}, [playerBoxRef, onPlayerTimeChanged]);

	const onUpdateSummaryData = useCallback((updatedBlocks: SummaryBlock[]): Promise<void> => {
		meetingInfo.summaryMeta = meetingInfo.summaryMeta || new SummaryMeta();

		const updateSummaryRequest = new UpdateSummaryRequest({
			summaryBlocks: updatedBlocks,
			meta: meetingInfo.summaryMeta,
		});

		const updatedMeetingsInfo = new MeetingInfo({
			...meetingInfo
			, 
			summaryBlocks: updatedBlocks,
			summaryMeta: meetingInfo.summaryMeta,
		});
		setMeetingInfo(updatedMeetingsInfo);

		return apiInstance.updateSummaries(meetingId, updateSummaryRequest)
			.then((summaryBlocksFromApi) => {
				const updatedMeetingsInfo = new MeetingInfo(meetingInfo);
				updatedMeetingsInfo.summaryBlocks = summaryBlocksFromApi;
				setMeetingInfo(updatedMeetingsInfo);
			});
	}, [meetingInfo, meetingId, setMeetingInfo]);

	const onAgendaUpdated = useCallback(async (updatedAgenda: Agenda) => {
		const updatedMeetingsInfo = new MeetingInfo({
			...meetingInfo,
			agenda: updatedAgenda,
		});
		setMeetingInfo(updatedMeetingsInfo);

		const agendaFromApi = await apiInstance.updateAgenda(meetingId, updatedAgenda);

		const updatedMeetingsInfoFromApi = new MeetingInfo({
			...meetingInfo,
			agenda: agendaFromApi,
		});
		setMeetingInfo(updatedMeetingsInfoFromApi);

	}, [meetingInfo, meetingId, setMeetingInfo]);

	const downloadProtocol = useCallback(async () => {
		setIsDownloadingResume(true);
		try {
			const startTime = moment(meetingInfo.startUtc).format("DD.MM.YYYY HH.mm");
			const fileResponse = await apiInstance.getMeetingMinutes(meetingId);
			downloadFile(fileResponse.data, `Протокол ${startTime} ${meetingInfo.subject}.docx`);
		} finally {
			setIsDownloadingResume(false);
		}
	}, [meetingId, meetingInfo]);
	
	
	

	const downloadUserFile = useCallback((filename: string): Promise<void> => {
		return apiInstance.downloadUserFile(meetingId, filename)
			.then((fileResponse) => {
				downloadFile(fileResponse.data, filename);
			});
	}, [meetingId]);

	const deleteUserFile = useCallback(async (filename: string): Promise<void> => {
		const fileNameStr = <span className="toast_fileName">{filename}</span>;
		try {
			const updatedUserFiles = await apiInstance.deleteUserFile(meetingId, filename);
			setUserFileNames(updatedUserFiles);
			toast.info(<div>Файл «{fileNameStr}» удалён</div>, { autoClose: ToastFastInterval});
		} catch (ex) {
			toast.error(<div>Ошибка при удалении файла «{fileNameStr}»: {ex.toString()}</div>, {autoClose: false });
		}

	}, [meetingId]);

	const uploadUserFile = useCallback(async (files: File[]) => {
		const userFileParameters: FileParameter[] = []
		
		files.forEach(file => {
			userFileParameters.push({
				fileName: file.name,
				data: file
			});
		});

		const pluralFiles = files.length > 1;
		const fileNamesStr = <ul className='toast_fileName'>{files.map(f => <li key={f.name}>{f.name}</li>)}</ul>

		try {
			const updatedUserFiles = await apiInstance.uploadUserFile(meetingId, userFileParameters);
			setUserFileNames(updatedUserFiles);
			toast.info(<div>{pluralFiles ? "Файлы" : "Файл"} {fileNamesStr} {pluralFiles ? "добавлены" : "добавлен"}</div>, {autoClose: ToastFastInterval});
		} catch (ex) {
			toast.error(`Ошибка при добавлении ${pluralFiles ? "файлов" : "файла"} ${fileNamesStr}: ${ex}`, {autoClose: ToastFastInterval });
		}
	}, [meetingId]);


	const purgeSummary = useCallback(() => {
		const updateSummaryRequest = new UpdateSummaryRequest({
			meta: new SummaryMeta(),
			summaryBlocks: [],
		});

		return apiInstance.updateSummaries(meetingId, updateSummaryRequest)
			.then((summaryBlocksFromApi) => {
				const updatedMeetingsInfo = new MeetingInfo(meetingInfo);
				updatedMeetingsInfo.summaryBlocks = summaryBlocksFromApi;
				setMeetingInfo(updatedMeetingsInfo);
				toast(`Протокол очищен`);
			});
	}, [meetingId, meetingInfo, setMeetingInfo]);
	
	
	const requestASR = useCallback((request: RecordingUpdateInfo): Promise<void> => {
		const result = apiInstance.requestASR(request);
		toast(`Запрошено распознавание ${request.asrName}`);
		return result;
	}, []);

	const cloneMeeting = useCallback(async (request: DuplicateRequest): Promise<void> => {
		
		const cloningToastId = toast('Клонирую собрание...', { autoClose: false })
		try
		{
			const newMeetingId = await apiInstance.duplicate(meetingId, request);
			toast(<div>Собрание клонировано: <Link target='_blank' to={routes.meetingPage.getPath(newMeetingId)}>{newMeetingId}</Link></div>, { autoClose: false });
		}
		finally
		{
			if (cloningToastId) {
				toast.dismiss(cloningToastId);
			}
		}

	}, [meetingId]);

	

	const generateAutoResume = useCallback(async () => {
		setIsGeneratingAutoResume(true);
		try {
			await apiInstance.makeAuto(meetingId, {});
				
			const actualMeetingInfo = await apiInstance.getMeetingInfo(meetingId);

			const updatedMeetingsInfo = new MeetingInfo({
				...meetingInfo,
				summaryBlocks: actualMeetingInfo.summaryBlocks,
				summaryMeta: actualMeetingInfo.summaryMeta,
			});

			setMeetingInfo(updatedMeetingsInfo);
			setIsGeneratingAutoResume(false);
		}
		catch(ex) {
			toast(`Ошибка генерации протокола: ${ex}`, {autoClose: false });
		}
		finally {
			setIsGeneratingAutoResume(false);
		}

	}, [meetingId, meetingInfo, setMeetingInfo]);

	const generateAutoResumeFineTuned = useCallback((fineTuneParams: string): Promise<void> => {
		setIsGeneratingAutoResume(true);
		return apiInstance.makeAuto(meetingId, JSON.parse(fineTuneParams))
			.then(async () => {
				const actualMeetingInfo = await apiInstance.getMeetingInfo(meetingId);

				const updatedMeetingsInfo = new MeetingInfo({
					...meetingInfo,
					summaryBlocks: actualMeetingInfo.summaryBlocks,
					summaryMeta: actualMeetingInfo.summaryMeta,
				});

				setMeetingInfo(updatedMeetingsInfo);
				setIsGeneratingAutoResume(false);
			});
	}, [meetingId, meetingInfo, setMeetingInfo]);

	const setUserVoice = useCallback((userVoice: File): Promise<void> => {
		if (userVoice) {
			const userVoiceFileParam: FileParameter = {
				fileName: "voice-sample.webm",
				data: userVoice
			};
	
			toast('Обновляю слепок голоса');
			return apiInstance.setUserVoice(userVoiceFileParam)
				.then(async () => {
					toast('Слепок голоса обновлён');
				});

		} else {
			return apiInstance.setUserVoice(null)
				.then(async () => {
					toast('Слепок голоса забыт');
				});
		}
	}, []);


	const onUpdateMeetingInfo = useCallback(async (updatedMeetingInfo: UpdateMeetingInfoRequest): Promise<void> => {
		const locations = updatedMeetingInfo.location ? [new LocationInfo({id: GUID(), title: updatedMeetingInfo.location })] : [];
		const newMeetingsInfoLucky = new MeetingInfo({
			...meetingInfo,
			...updatedMeetingInfo,
			locations: locations,
		});
		onGetNewMeetingInfo(newMeetingsInfoLucky);

		const actualInfo = await apiInstance.updateInfo(meetingId, updatedMeetingInfo);
			
		const newMeetingsInfo = new MeetingInfo({
			...meetingInfo,
			...actualInfo,
		});
		onGetNewMeetingInfo(newMeetingsInfo);

	}, [meetingId, meetingInfo, onGetNewMeetingInfo]);

	const onTaskModal_Complete = useCallback(async (sendTasks: boolean, selectedTasks: string[]) => {
		if (sendTasks) {
			await apiInstance.completeAndSendTasks(meetingId, selectedTasks);
		} else {
			await apiInstance.fireStatusAction(meetingId, MeetingActions.Complete);
		}
		await fetchMeetingInfo();
	}, [meetingId, fetchMeetingInfo]);

	const onStatusAction = useCallback(async (action: MeetingActions) => {
		if (action == MeetingActions.Complete && meetingInfo && extractTasks(meetingInfo.summaryBlocks, true).length) {
			openSendTasksModal();
		} else {
			await apiInstance.fireStatusAction(meetingId, action);
			await fetchMeetingInfo();
		}
	}, [meetingId, fetchMeetingInfo, openSendTasksModal, meetingInfo]);

	const dialogueActsToTranscriptTextJson = useCallback((updatedDialogueActsList: ConvertedDialogueAct[]): TranscriptTextJson => {
		const transcriptText = TranscriptTextJson.fromJS(meetingInfo.transcriptText as unknown as ITranscriptTextJson);
		const speakers: {[key: string]: SpeakerTranscriptJson} = {};

		transcriptText.speakers.forEach((speakerTranscript) => {
			speakerTranscript.speaker = new SpeakerTranscriptJson_Speaker(speakerTranscript.speaker);
			speakers[speakerTranscript.speaker.id] = speakerTranscript;
		});

		const newSpeakerRows: {[key: string]: SpeakerTranscriptJson} = {};

		updatedDialogueActsList.forEach(dialogueAct => {
			if (!newSpeakerRows[dialogueAct.data.speakerId]) {
				if (speakers[dialogueAct.data.speakerId]) {
					const initialTranscript = new SpeakerTranscriptJson({
						...speakers[dialogueAct.data.speakerId]
					});
					initialTranscript.rows = [];
					newSpeakerRows[dialogueAct.data.speakerId] = initialTranscript;
				} else {
					// const sourceSpeakerTranscript = transcriptText.speakers.filter(speaker => {
					// 	return (speaker.rows as undefined as ITranscriptUtterance[]).filter(row => isEqual(dialogueAct.initialData, row.)).length > 0;
					// });

					const initialTranscript = new SpeakerTranscriptJson({						
						rows: [],
						speaker: new SpeakerTranscriptJson_Speaker({
							id: dialogueAct.data.speakerId,
							name: dialogueAct.data.name,
						}),
					});
					newSpeakerRows[dialogueAct.data.speakerId] = initialTranscript;
				}
			}

			const rows = newSpeakerRows[dialogueAct.data.speakerId].rows;

			const newUtt = SpeakerTranscriptJson_Utt.fromJS({
				...dialogueAct.initialData,
			});
			newUtt.text = dialogueAct.data.text;

			rows.push(newUtt);
		});

		transcriptText.speakers = Object.values(newSpeakerRows);

		return transcriptText;

	}, [meetingInfo]);

	const downloadTranscript = useCallback(async () => {
		setIsDownloadingTranscript(true);
		try {
			const startTime = moment(meetingInfo.startUtc).format("DD.MM.YYYY HH.mm");
			const transcriptText = dialogueActsToTranscriptTextJson(dialogueActs);
			const updatedTranscriptText = JSON.stringify(transcriptText);

			const fileResponse = await  apiInstance.getTranscriptReport(meetingId, updatedTranscriptText);
			downloadFile(fileResponse.data, `Стенограмма ${startTime} ${meetingInfo.subject}.docx`);
		} finally {
			setIsDownloadingTranscript(false);
		}
	}, [meetingId, meetingInfo, dialogueActs, dialogueActsToTranscriptTextJson]);

	const downloadTranscriptJson = useCallback(async () => {
		setIsDownloadingTranscriptJson(true);
		try {
			const transcriptText = dialogueActsToTranscriptTextJson(dialogueActs);
			
			const mergedText = transcriptText.speakers.flatMap(s => s.rows.map(utt => {
				return {
					startTicks: utt.startTicks,
					speakerId: s.speaker.id,
					text: utt.text,
					durationTicks: utt.durationTicks,
				}
			}));

			const speakers = [...meetingInfo.participants];

			transcriptText.speakers.forEach(s => {
				if (!speakers.some(speaker => speaker.id == s.speaker.id)) {
					speakers.push(new ParticipantInfo({
						id: s.speaker.id,
						name: s.speaker.name,
					}));
				}
			});

			const result = {
				participants: speakers,
				transcript: mergedText.sort((a, b) => a.startTicks - b.startTicks),
				totalTicks: 0,
				meetingDateUtc: meetingInfo.startUtc,
			};

			result["totalTicks"] = result.transcript[result.transcript.length-1].startTicks + result.transcript[result.transcript.length-1].durationTicks
			downloadFile(new Blob([new TextEncoder().encode(JSON.stringify(result))], {type: "application/json;charset=utf-8"}), `${meetingInfo.id}.json`);

		} finally {
			setIsDownloadingTranscriptJson(false);
		}
	}, [dialogueActs, meetingInfo, dialogueActsToTranscriptTextJson]);

	const onUpdateDialogueActsList = useCallback(async (updatedDialogueActsList: ConvertedDialogueAct[]) => {
		setDialogueActs(updatedDialogueActsList);

		const transcriptText = dialogueActsToTranscriptTextJson(updatedDialogueActsList);
		const updatedTranscriptText = JSON.stringify(transcriptText);
		const updateRequest = new MeetingTranscript_Request({
			transcriptText: updatedTranscriptText,
			speakers: transcriptText.speakers.map(s => s.speaker),
		});

		const apiTranscriptInfo = await apiInstance.updateTranscript(meetingId, updateRequest);

		setMeetingInfo({
			...meetingInfo, 
			...{
				transcriptMeta: apiTranscriptInfo.transcriptMeta,
				transcriptText: apiTranscriptInfo.transcriptText,
				participants: apiTranscriptInfo.participants,
			}
		} as MeetingInfo);
	}, [meetingId, meetingInfo, dialogueActsToTranscriptTextJson, setMeetingInfo]);

	// computed
	const canEditDialogueAct = useMemo((): boolean => {
		return (
				UserStoreState.rights.includes(UserRights.edit_meeting_transcript) || 
				(UserStoreState.rights.includes(UserRights.edit_own_meeting_transcript) && meetingInfo.isOwn)
			)
			&& meetingInfo && meetingInfo.status 
			&& (meetingInfo.status.alias == MeetingStatuses.InWork || meetingInfo.status.alias == MeetingStatuses.New);
	}, [UserStoreState.rights, meetingInfo]);

	const isProcessing = useMemo((): boolean => {
		return  meetingInfo && meetingInfo.status 
			&& !(   meetingInfo.status.alias == MeetingStatuses.InWork
				|| meetingInfo.status.alias == MeetingStatuses.New
				|| meetingInfo.status.alias == MeetingStatuses.Done);
	}, [meetingInfo]);

	const hasRightsToEditResume = useMemo((): boolean => {
		return (
			UserStoreState.rights.includes(UserRights.edit_meeting_summary) || 
			(UserStoreState.rights.includes(UserRights.edit_own_meeting_summary) && meetingInfo.isOwn)
		);
	}, [UserStoreState.rights, meetingInfo]);

	const canEditResume = useMemo((): boolean => {
		return meetingInfo && meetingInfo.status && 
		(meetingInfo.status.alias == MeetingStatuses.InWork || meetingInfo.status.alias == MeetingStatuses.New)
		&& hasRightsToEditResume;
	}, [hasRightsToEditResume, meetingInfo]);

	// const canSendResume = useMemo((): boolean => {
	// 	return meetingInfo && meetingInfo.status && 
	// 	(meetingInfo.status.alias == MeetingStatuses.Done)
	// 	&& hasRightsToEditResume;
	// }, [hasRightsToEditResume, meetingInfo]);

	const canEditFiles = useMemo((): boolean => {
		return meetingInfo && meetingInfo.status && 
		(meetingInfo.status.alias == MeetingStatuses.InWork || meetingInfo.status.alias == MeetingStatuses.New)
		&& (
			UserStoreState.rights.includes(UserRights.edit_files) || 
			(UserStoreState.rights.includes(UserRights.edit_own_files) && meetingInfo.isOwn)
		);
	}, [UserStoreState.rights, meetingInfo]);

	const canEditAgenda = useMemo((): boolean => {
		return meetingInfo && meetingInfo.status && 
		(meetingInfo.status.alias == MeetingStatuses.InWork || meetingInfo.status.alias == MeetingStatuses.New)
		&& (
			UserStoreState.rights.includes(UserRights.edit_agenda) || 
			(UserStoreState.rights.includes(UserRights.edit_own_agenda) && meetingInfo.isOwn)
		);
	}, [UserStoreState.rights, meetingInfo]);

	// views

	const playerView = useMemo((): JSX.Element => {
		if (!meetingInfo) {
			return <Skeleton content="загрузка" />
		}

		const isPlannedOrRecording = (meetingInfo?.status.alias == MeetingStatuses.Plan || meetingInfo?.status.alias == MeetingStatuses.Record);
		if (isPlannedOrRecording) {
			return null;
		}

		return (
			<div className="meeting-page__player">
				<MeetingPlayer
					onIsPlayingChanged={onPlayerPlayStateChanged}
					mediaUrl={meetingInfo.hasAudio ? `/api/media/${meetingId}/audio?access_token=${accessToken}` : ""}
					onTimeChanged={onPlayerTimeChanged}
					setNeedScroll={toggleNeedScroll}
					playerRef={playerBoxRef}
					needScroll={needScroll}
				/>
			</div>
		);
	}, [onPlayerPlayStateChanged, accessToken, onPlayerTimeChanged, toggleNeedScroll, meetingInfo, playerBoxRef, needScroll, meetingId]);

	const dialogueActsView = useMemo((): JSX.Element => {	
		return (
			<DialogueActsList
				onUpdateDialogueActsList={onUpdateDialogueActsList}
				canEditDialogueAct={canEditDialogueAct}
				onPauseDAClick={onPauseDAClick}
				onPlayDAClick={onPlayDAClick}
				setNeedScroll={setNeedScroll}
				needScroll={needScroll}
				isPlaying={isPlaying}
				data={dialogueActs}
				canEditResume={canEditResume}
				downloadTranscriptAction={downloadTranscript}
				downloadTranscriptJsonAction={downloadTranscriptJson}
				accessToken={accessToken}
				isDownloadingTranscript={isDownloadingTranscript}
				isDownloadingTranscriptJson={isDownloadingTranscriptJson}
				isProcessing={isProcessing}
				isDeveloper={UserStoreState.rights.includes(UserRights.developer)}
			/>
		);
	}, [canEditDialogueAct, canEditResume, onPauseDAClick, onPlayDAClick,
		dialogueActs, needScroll, isPlaying, onUpdateDialogueActsList,
		downloadTranscript, accessToken, isDownloadingTranscript,
		downloadTranscriptJson, isDownloadingTranscriptJson,
		isProcessing, UserStoreState.rights]);

	const toggleShowAgendaInSummary = useCallback(async () => {
		const newAgenda: Agenda = cloneDeep(meetingInfo.agenda);
		const newSummaryBlocks: SummaryBlock[] = cloneDeep(meetingInfo.summaryBlocks);

		newAgenda.showInSummary = !meetingInfo.agenda.showInSummary;
		newSummaryBlocks && newSummaryBlocks.forEach(summaryBlock => {
			summaryBlock.items.forEach(item => {
				item.agendaItemId = null;
			})
		});

		const updateSummaryRequest = new UpdateSummaryRequest({
			meta: new SummaryMeta(),
			summaryBlocks: newSummaryBlocks,
		});

		const toggleRequest = new ToggleShowAgendaRequest({
			summaryRequest: updateSummaryRequest,
			agenda: newAgenda,
		});

		const updatedMeetingsInfo = new MeetingInfo({
			...meetingInfo,
			agenda: newAgenda,
			summaryBlocks: newSummaryBlocks
		});
		setMeetingInfo(updatedMeetingsInfo);

		const summaryFromApi = await apiInstance.toggleShowAgenda(meetingId, toggleRequest)
			
		const apiUpdatedMeetingsInfo = new MeetingInfo({
			...meetingInfo,
			summaryBlocks: newSummaryBlocks,
			agenda: summaryFromApi.agenda,
		});
		setMeetingInfo(apiUpdatedMeetingsInfo);

	}, [meetingInfo, meetingId, setMeetingInfo]);

	const summaryAreaView = useMemo((): JSX.Element => {
		let summaryView = null;
		let summaryTrailingView: JSX.Element = null;
		let agendaTrailingView: JSX.Element = null;
		let agendaView = null;
		let filesView = null;

		if (!meetingInfo) {
			summaryView = <Skeleton content="Загрузка..." />;
			agendaView = <Skeleton content="Загрузка..." />;
			filesView = <Skeleton content="Загрузка..." />;
		} else {
			const isPlannedOrRecording = (meetingInfo?.status.alias == MeetingStatuses.Plan || meetingInfo?.status.alias == MeetingStatuses.Record);
			if (isPlannedOrRecording) {
				summaryView = null;
			}
			else {
				const summaryBlocks = makeDefaultSummaryBlocks(meetingInfo.summaryBlocks);
				processSummaryBlocks(summaryBlocks, meetingInfo.agenda);
				const canDownloadSummary = summaryBlocks && summaryBlocks.some((summaryBlock) => summaryBlock.items.length > 0);
				const hideSendTasks = false;
				const tasks = extractTasks(summaryBlocks, hideSendTasks);

				const taskListView = <div className='inline-tasks'>
					<div className='inline-tasks__title'>Задания на исполнение</div>
					<TaskList
						className='inline-tasks__list'
						tasks={tasks}
						summaryBlocks={meetingInfo.summaryBlocks}
						onUpdateSummaryData={onUpdateSummaryData}
						canEditResume={canEditResume}
					/>
				</div>

				summaryTrailingView = 
				(
					<Space className='action-buttons'>
						{canEditResume &&
						<Popover className='task-list-popover back' content={taskListView}>
							<IconButton
								theme="ghost"
								className='task-list-btn'
								iconSize='24px'
								title="Задания на исполнение"
								icon={<ListIcon />}
							/>
						</Popover>
						}
						<IconButton
							theme="ghost"
							disabled={!canDownloadSummary}
							onClick={downloadProtocol}
							isBusy={isDownloadingResume}
							title="Скачать протокол"
							iconSize='24px'
							icon={<DownloadIcon />}
							/>
						{!SummeetConfig.hideSummarization && meetingInfo?.status.alias != MeetingStatuses.Done ?
							<Button 
								theme="primary"
								onClick={generateAutoResume}
								disabled={!canEditResume}
								isBusy={isGeneratingAutoResume}
							>
								Авторезюме
							</Button>
						: null
						}
					</Space>
				);

				summaryView = <SummaryList
					onUpdateSummaryData={onUpdateSummaryData}
					canEditResume={canEditResume}
					summaryBlocks={summaryBlocks}
					agenda={meetingInfo.agenda}
					onHighlightihgChanged={onHighlightihgChanged}
				/>

				agendaView = <AgendaList
					canEditAgenda={canEditAgenda}
					agenda={meetingInfo.agenda}
					onAgendaUpdated={onAgendaUpdated} 
				/>;
				filesView = userFileNames ? <FileView
					canEditFiles={canEditFiles}
					fileInfos={userFileNames}
					onDownload={downloadUserFile}
					onDelete={deleteUserFile}
					onUpload={uploadUserFile}
				/> : null;

				agendaTrailingView = meetingInfo.agenda && canEditAgenda ? 
					<Button theme='ghost' onClick={toggleShowAgendaInSummary}>{meetingInfo.agenda.showInSummary ? "⨯ Скрыть в протоколе" : "👁 Отображать в протоколе"}</Button>
					: <div></div>;
			}
		}

		return (
			<Tabs theme='primary' kind="horizontal" className='meeting-tabs' defaultIndex={1}>
				<Tab
					title="Повестка"
					trailing={agendaTrailingView}
					hidden={SummeetConfig.hideAgenda || (!canEditAgenda && meetingInfo && (!meetingInfo.agenda || !meetingInfo.agenda.items || !meetingInfo.agenda.items.length))}
				>
					{agendaView}
				</Tab>
				<Tab title="Протокол" trailing={summaryTrailingView}>
					{summaryView}
				</Tab>
				<Tab
					title="Материалы"
					trailing={<div></div>}
					hidden={!canEditFiles && meetingInfo && (!userFileNames || !userFileNames.length)}
				>
					{filesView}
				</Tab>
			</Tabs>
		);
	}, [meetingInfo, canEditResume, downloadProtocol, userFileNames, downloadUserFile, deleteUserFile,
		isGeneratingAutoResume, onUpdateSummaryData, generateAutoResume, toggleShowAgendaInSummary,
		uploadUserFile, onAgendaUpdated, canEditFiles, canEditAgenda, onHighlightihgChanged,
		isDownloadingResume]);

	const onChangeTranscriptRequestOptions = useCallback(async (newOptions: TranscriptRequestOptions) => {
		setTranscriptRequestOptions(newOptions);
	}, [setTranscriptRequestOptions]);

	const onPurgeObsoleteMedia = useCallback(() => {
		apiInstance.maintenancePurgeObsoleteMedia();
	}, []);

	const onDeleteMeeting = useCallback(async () => {
		await apiInstance.deleteMeeting(meetingId);
		history.push(routes.meetingListPage.path);
	}, [meetingId, history]);
	

	const meetingDevModalView =  useMemo((): JSX.Element => {
		return isDevModalOpen && (
			<MeetingDevModal
				closeModal={closeDevModal}
				transcriptRequestOptions={transcriptRequestOptions}
				changeTranscriptRequestOptions={onChangeTranscriptRequestOptions}
				meetingInfo={meetingInfo}
				purgeSummary={purgeSummary}
				requestASR={requestASR}
				cloneMeeting={cloneMeeting}
				makeFineTunnedAuto={generateAutoResumeFineTuned}
				setUserVoice={setUserVoice}
				purgeObsoleteMedia={onPurgeObsoleteMedia}
				deleteMeeting={onDeleteMeeting}
				playerBoxRef={playerBoxRef}
			/>);
		}, [closeDevModal, meetingInfo, isDevModalOpen,
			onChangeTranscriptRequestOptions, transcriptRequestOptions, requestASR,
			cloneMeeting, generateAutoResumeFineTuned, setUserVoice, purgeSummary,
			onPurgeObsoleteMedia, onDeleteMeeting]);

	const sendTasksModalView = useMemo(() => {
		return isSendTasksModalOpen && <SendTasksModal 
			closeModal={closeSendTasksModal}
			onUpdateSummaryData={onUpdateSummaryData}
			summaryBlocks={meetingInfo.summaryBlocks}
			onComplete={onTaskModal_Complete}
			canEditResume={canEditResume}
		/>
	}, [isSendTasksModalOpen, closeSendTasksModal, onTaskModal_Complete, onUpdateSummaryData, meetingInfo, canEditResume]);

	const contentView = useMemo((): JSX.Element => {
		if (!meetingInfo) {
			return <div className="meeting-page__load">
				<Skeleton content="Идёт загрузка собрания..." />
			</div>;
		}

		return (
			<div className="meeting-page">
				<MeetingPageHeader 
					meetingInfo={meetingInfo}
					onStatusAction={onStatusAction}  
					onUpdateInfo={onUpdateMeetingInfo}
				/>
				<div className="meeting-page__content">
					<>
						{sendTasksModalView}
						{meetingDevModalView}
					</>
					<div className='meeting-page__content-columns'>
						<div className='meeting-page__content_part'>{dialogueActsView}</div>
						<div className='meeting-page__content_part'>{summaryAreaView}</div>
					</div>
					{playerView}
				</div>
			</div>
		);
	}, [meetingInfo, playerView, dialogueActsView, summaryAreaView, onStatusAction,
		meetingDevModalView, sendTasksModalView, onUpdateMeetingInfo]);

	return (
		<AppPageLayout
			content={contentView}
		/>
	);
}

export {MeetingPage};